import { useMutation } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';

import EditPlanModal from '@/components/modal/plans/EditPlansModal';
import Table from '@/components/table/common/Table';
import { PlanItem } from '@/interfaces/api/configApp/IPlanItem';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const columnHelper = createColumnHelper<PlanItem>();

const PlansTable = ({
	plans,
	refetch,
}: {
	plans: PlanItem[];
	refetch: () => void;
}) => {
	const [selectedPlan, setSelectedPlan] = useState<PlanItem | null>(null);

	const updatePlanMutation = useMutation({
		mutationFn: (updatedPlan: PlanItem) =>
			haggleApiService.updateConfig(updatedPlan.planName, {
				value: updatedPlan,
			}),
		onSuccess: () => {
			notificationService.success('Plan updated successfully');
			setSelectedPlan(null);
			refetch(); // Trigger data refetch after successful update
		},
		onError: (err: any) => {
			notificationService.error(err?.message || 'Failed to update plan');
		},
	});

	const handleSave = (updatedPlan: PlanItem) => {
		updatePlanMutation.mutate(updatedPlan); // Use mutation here
	};

	const columns = [
		columnHelper.accessor('name', {
			header: () => (
				<div className="text-start w-[20rem] break-words">Plan Name</div>
			),
			cell: (info) => (
				<div className="text-start w-[20rem] break-words">
					{info.getValue()}
				</div>
			),
		}),
		columnHelper.accessor('limit', {
			header: () => <div className="text-center w-32 break-words">Limit</div>,
			cell: (info) => (
				<div className="text-center w-32 break-words">{info.getValue()}</div>
			),
		}),
		columnHelper.accessor('price', {
			header: () => (
				<div className="text-center w-40 break-words">Price ($)</div>
			),
			cell: (info) => (
				<div className="text-center w-40 break-words">{info.getValue()}</div>
			),
		}),
		columnHelper.accessor('pricePerExtraHaggle', {
			header: () => (
				<div className="text-center w-48 break-words">
					Price per Extra Haggle ($)
				</div>
			),
			cell: (info) => (
				<div className="text-center w-48 break-words">{info.getValue()}</div>
			),
		}),
		{
			id: 'actions',
			header: () => <div className="text-center w-24 break-words">Actions</div>,
			cell: (info: any) => (
				<div className="flex justify-center w-24">
					<button
						className="text-greenBrand-500 underline"
						onClick={() => setSelectedPlan(info.row.original)}
					>
						Edit
					</button>
				</div>
			),
		},
	];

	return (
		<div className="flex flex-col items-center w-full bg-white p-4 rounded-md shadow-md">
			<div className="w-full mb-4">
				<h3 className="text-lg font-semibold pb-4 border-b border-gray-200">
					Configure Plans
				</h3>
			</div>

			{plans && (
				<Table<PlanItem>
					collection={{
						data: plans,
						pageNumber: 1,
						pageSize: plans.length,
						pageCount: 1,
						itemCount: plans.length,
					}}
					columns={columns}
					tableStyles={{
						tableBox: 'w-full overflow-x-auto',
						table: 'w-full',
					}}
				/>
			)}

			{selectedPlan && (
				<EditPlanModal
					plan={selectedPlan}
					onClose={() => setSelectedPlan(null)}
					onSave={handleSave}
				/>
			)}
		</div>
	);
};

export default PlansTable;
